import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { getAccessToken } from '../utils/auth';
import { listReferenceRows } from '../utils/httpApi';
import { isApiSuccess } from '../utils/api';
import './AdminReferenceData.css';

/** Default search: ICAO aircraft type designators, labelled "TYPE — manufacturer_model". */
export async function searchAircraftTypes(q) {
  const resp = await listReferenceRows({ table: 'icao_aircraft_type', q, page: 0, pageSize: 20 }, getAccessToken());
  if (!isApiSuccess(resp)) return [];
  return (resp.rows || []).map(row => ({
    value: row.type_designator,
    label: `${row.type_designator} — ${row.manufacturer_model || ''}`.trim(),
    row
  }));
}

/**
 * Typeahead picker backed by a server search. Emits `onSelect({value, label, row})`
 * when an option is chosen and `onSelect(null)` when cleared.
 *
 * Keyboard: ArrowUp/Down move, Enter picks, Escape closes. Click outside closes.
 */
function AdminSearchSelect({
  value,
  onSelect,
  search = searchAircraftTypes,
  placeholder = 'Search type…',
  debounceMs = 250,
  minChars = 1,
  disabled = false,
  ariaLabel = 'Search'
}) {
  const [text, setText] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);
  const requestId = useRef(0);
  const listId = useId();

  // Keep the box in sync when the parent changes the value (prefill, reset).
  useEffect(() => { setText(value || ''); }, [value]);

  const runSearch = useCallback(async (q) => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const found = await search(q);
      if (id !== requestId.current) return; // stale
      setOptions(found || []);
      setActive(found && found.length ? 0 : -1);
    } catch (e) {
      if (id === requestId.current) setOptions([]);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [search]);

  // Debounced search while the popup is open.
  useEffect(() => {
    if (!open) return undefined;
    const q = text.trim();
    if (q.length < minChars) { setOptions([]); return undefined; }
    const t = setTimeout(() => runSearch(q), debounceMs);
    return () => clearTimeout(t);
  }, [text, open, minChars, debounceMs, runSearch]);

  // Click outside closes.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const choose = (opt) => {
    setText(opt.value);
    setOpen(false);
    onSelect(opt);
  };

  const clear = () => {
    setText('');
    setOptions([]);
    setOpen(false);
    onSelect(null);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(i => (options.length ? (i + 1) % options.length : -1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(i => (options.length ? (i - 1 + options.length) % options.length : -1));
    } else if (e.key === 'Enter') {
      if (active >= 0 && options[active]) { e.preventDefault(); choose(options[active]); }
    }
  };

  const q = text.trim();
  const showList = open && q.length >= minChars;

  return (
    <div className="admin-search-select" ref={rootRef}>
      <input
        type="text"
        className="admin-search-select-input"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        onChange={e => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
      {text && !disabled && (
        <button type="button" className="admin-search-select-clear" aria-label="Clear" onClick={clear}>×</button>
      )}
      {showList && (
        <ul className="admin-search-select-list" role="listbox" id={listId}>
          {loading && options.length === 0 && (
            <li className="admin-search-select-empty">Searching…</li>
          )}
          {!loading && options.length === 0 && (
            <li className="admin-search-select-empty">No matches</li>
          )}
          {options.map((opt, i) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={i === active}
              className={`admin-search-select-option${i === active ? ' active' : ''}`}
              onMouseDown={e => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(opt)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AdminSearchSelect;
